/**
 * <menu-drawer-drilldown>
 *
 * Turquoise mobile menu: two-level drill-down (mockup #mm-drawer).
 * Level 0 = search + category list. Tapping a category that has children
 * slides to its level-1 sub-panel; the back button returns to level 0.
 *
 * All panels are server-rendered (SSR) — this element only toggles classes,
 * so it degrades gracefully (parent buttons fall back to their `href` link
 * via the sub-panel's "view all" link if JS is unavailable).
 *
 * Drawer open/close, Esc and scroll-lock are handled by the parent
 * <drawer-element>; this only manages the level transition.
 */
class MenuDrawerDrilldown extends HTMLElement {
  #openers = [];
  #backs = [];
  #panels = [];

  connectedCallback() {
    this.#openers = Array.from(this.querySelectorAll('[data-mdd-open]'));
    this.#backs = Array.from(this.querySelectorAll('[data-mdd-back]'));
    this.#panels = Array.from(this.querySelectorAll('[data-mdd-panel]'));

    for (const opener of this.#openers) opener.addEventListener('click', this.#onOpen);
    for (const back of this.#backs) back.addEventListener('click', this.#onBack);

    // Reset to level 0 whenever the drawer is (re)opened, so a previous
    // drill-down doesn't persist. drawer-element publishes via aria-hidden.
    this.#drawer = this.closest('[id="menu-drawer"], drawer-element');
    if (this.#drawer) {
      this.#observer = new MutationObserver(() => {
        // Reset to level 0 on every open/close toggle (not just close), so the
        // drawer never reopens in a previously drilled state. NGE-8051.
        this.#reset();
      });
      this.#observer.observe(this.#drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
  }

  disconnectedCallback() {
    for (const opener of this.#openers) opener.removeEventListener('click', this.#onOpen);
    for (const back of this.#backs) back.removeEventListener('click', this.#onBack);
    this.#observer?.disconnect();
  }

  #drawer = null;
  #observer = null;

  #onOpen = (event) => {
    const id = event.currentTarget.dataset.mddOpen;
    const panel = this.#panels.find((p) => p.dataset.mddPanel === id);
    if (!panel) return;
    for (const p of this.#panels) {
      const active = p === panel;
      p.classList.toggle('is-active', active);
      p.setAttribute('aria-hidden', active ? 'false' : 'true');
    }
    this.classList.add('is-drilled');
    panel.querySelector('[data-mdd-back]')?.focus();
  };

  #onBack = () => this.#reset(true);

  #reset = (focusOpener) => {
    if (!this.classList.contains('is-drilled')) return;
    const activePanel = this.#panels.find((p) => p.classList.contains('is-active'));
    this.classList.remove('is-drilled');
    for (const p of this.#panels) {
      p.classList.remove('is-active');
      p.setAttribute('aria-hidden', 'true');
    }
    if (focusOpener && activePanel) {
      this.#openers.find((o) => o.dataset.mddOpen === activePanel.dataset.mddPanel)?.focus();
    }
  };
}

if (!customElements.get('menu-drawer-drilldown')) {
  customElements.define('menu-drawer-drilldown', MenuDrawerDrilldown);
}
